/**
 * Pointer drag to reorder vault categories (mouse / touch). A short click still
 * opens the category; past a small slop the row follows the finger/cursor and
 * the list reorders live. Persist on pointer-up via `onCommit`.
 */
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'

const DRAG_SLOP = 8

export function useCategoryReorder({
  enabled,
  getIds,
  onReorder,
  onCommit,
}: {
  enabled: boolean
  /** Current order (read live so mid-drag swaps see the latest list). */
  getIds: () => string[]
  /** Live move while dragging (optimistic). */
  onReorder: (from: number, to: number) => void
  /** Save the current order after a drag ends. */
  onCommit: () => void
}) {
  const press = useRef<{ id: string; x: number; y: number } | null>(null)
  const dragIdRef = useRef<string | null>(null)
  const [dragId, setDragId] = useState<string | null>(null)
  const dirty = useRef(false)
  const skipClick = useRef(false)
  const latest = useRef({ getIds, onReorder, onCommit })
  latest.current = { getIds, onReorder, onCommit }

  useEffect(() => {
    if (!enabled) return
    const move = (e: PointerEvent) => {
      const p = press.current
      if (!p) return
      const { getIds: idsOf, onReorder: reorder } = latest.current
      if (!dragIdRef.current && Math.hypot(e.clientX - p.x, e.clientY - p.y) < DRAG_SLOP) return
      e.preventDefault()
      if (!dragIdRef.current) {
        dragIdRef.current = p.id
        setDragId(p.id)
        skipClick.current = true
      }
      const el = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-cat-id]') as HTMLElement | null
      const over = el?.dataset.catId
      if (!over || over === p.id) return
      const list = idsOf()
      const from = list.indexOf(p.id)
      const to = list.indexOf(over)
      if (from < 0 || to < 0 || from === to) return
      const box = el.getBoundingClientRect()
      const mid = box.top + box.height / 2
      // Only cross when the pointer passes the row midpoint (avoids flicker).
      if (from < to && e.clientY < mid) return
      if (from > to && e.clientY > mid) return
      reorder(from, to)
      dirty.current = true
    }
    const up = () => {
      press.current = null
      const wasDragging = Boolean(dragIdRef.current)
      dragIdRef.current = null
      setDragId(null)
      if (wasDragging && dirty.current) {
        dirty.current = false
        latest.current.onCommit()
      } else {
        dirty.current = false
      }
    }
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [enabled])

  return {
    dragId,
    bindRow: (id: string) => ({
      'data-cat-id': id,
      onPointerDown: (e: ReactPointerEvent) => {
        if (!enabled || e.button !== 0) return
        if ((e.target as HTMLElement).closest('button.lib-icon-btn')) return
        press.current = { id, x: e.clientX, y: e.clientY }
      },
      onClickCapture: (e: ReactMouseEvent) => {
        if (!skipClick.current) return
        skipClick.current = false
        e.preventDefault()
        e.stopPropagation()
      },
    }),
  }
}
